import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Snackbar,
  Alert,
  Card,
  CardContent,
  Divider,
  MenuItem,
  Select,
  FormControl,
  Tooltip,
} from "@mui/material";
import { School as SchoolIcon } from "@mui/icons-material";
import jsPDF from "jspdf";
import "jspdf-autotable";
import LoadingBar from "react-top-loading-bar";

const Result = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [resultData, setResultData] = useState([]);
  const [loadingBarProgress, setLoadingBarProgress] = useState(0);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [sessions, setSessions] = useState([]);
  const [session, setSession] = useState("");
  const [logoDataUrl, setLogoDataUrl] = useState("");
const [studentProfile, setStudentProfile] = useState(null);
const [studentAttendance, setStudentAttendance] = useState(null);
  const token = sessionStorage.getItem("token");
  const apiUrl = process.env.REACT_APP_BASE_URL;
  const classId = sessionStorage.getItem("classId");
  const sectionId = sessionStorage.getItem("sectionId");

  // Fetch financial years (sessions)
  const fetchSessions = async () => {
  try {
    setLoadingBarProgress(30);
    const response = await fetch(`${apiUrl}/ClassPromotion/GetFinancialYear`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const data = await response.json();
    setSessions(data);

    // Determine the current financial year
    const today = new Date();
    const currentYear = today.getFullYear();
    const nextYear = currentYear + 1;
    const financialYearStart = new Date(currentYear, 3, 1); // April 1
    const financialYearEnd = new Date(nextYear, 2, 31);   // March 31

    let currentSessionString;
    if (today >= financialYearStart && today <= financialYearEnd) {
      currentSessionString = `${currentYear}-${nextYear}`;
    } else {
      currentSessionString = `${currentYear - 1}-${currentYear}`;
    }

    // Find the matching session
    const currentSession = data.find(
      (item) => item.finanacialYear === currentSessionString
    );

    // Auto-select the current session
    if (currentSession) {
      setSession(currentSession.financialYearID);
    } else if (data.length > 0) {
      // Fallback: select the first session if current not found
      setSession(data[0].financialYearID);
    }

    setLoadingBarProgress(100);
  } catch (error) {
    console.error("Error fetching financial years:", error);
  }
};



  // Fetch school logo
  const fetchSchoolLogo = async () => {
    try {
      const response = await fetch(`${apiUrl}/FeeReport/GetSchoolName`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        throw new Error("Failed to fetch school logo");
      }

      const apiData = await response.json();
      const logoImg = apiData?.[0]?.logoImg;
      if (logoImg && typeof logoImg === "string" && logoImg.trim() !== "") {
        let cleanedBase64 = logoImg
          .replace(/^data:image\/[a-zA-Z]+;base64,/, "")
          .replace(/\s/g, "")
          .replace(/\n/g, "");
        const paddingLength = (4 - (cleanedBase64.length % 4)) % 4;
        cleanedBase64 += "=".repeat(paddingLength);
        setLogoDataUrl(`data:image/png;base64,${cleanedBase64}`);
      }
    } catch (error) {
      console.error("Error fetching school logo:", error);
    }
  };

  // Fetch result data
 const fetchResultData = async () => {
  if (!session || !classId || !sectionId) return;

  try {
    setIsLoading(true);
    const response = await fetch(`${apiUrl}/Result/GetStudentResultsGenerated`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
      body: JSON.stringify({
        sessionId: session,
        examTypeId: 0,
        classId,
        sectionId,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const data = await response.json();
    const studentRollNo = sessionStorage.getItem("rollNo");

    if (Array.isArray(data) && data.length > 0) {
      const studentResult = data.find(
        (student) => student.rollNo === studentRollNo
      );
      
      setResultData(studentResult ? [studentResult] : []);
    } else {
      setSnackbarMessage("No result data available.");
      setOpenSnackbar(true);
    }
  } catch (error) {
    setSnackbarMessage("Error fetching result data.");
    setOpenSnackbar(true);
    console.error("Error fetching result data:", error);
  } finally {
    setIsLoading(false);
  }
};

const fetchStudentProfile = async (studentId) => {
  try {
    setLoadingBarProgress(30);
    const response = await fetch(`${apiUrl}/Attendance/StudentProfile`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
      body: JSON.stringify({
        yearId: session,
        studentId,
      }),
    });

    if (!response.ok) {
      return;
    }

    const profileData = await response.json();
    const studentProfile = profileData.objData;
    const studentAttendance = profileData.totalAttendancList[0];
    setStudentProfile(studentProfile);
    setStudentAttendance(studentAttendance);

    const resultsResponse = await fetch(`${apiUrl}/Result/GetExamResults`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
      body: JSON.stringify({
        studentId,
        sessionId: session,
        examTypeId: 0,
        subExamId: 0,
        subjectId: 0,
      }),
    });

    if (!resultsResponse.ok) {
      setLoadingBarProgress(0);
      return;
    }
    setLoadingBarProgress(100);
    const resultsData = await resultsResponse.json();
    const results = resultsData.results;

    const subjectNames = [
      ...new Set(
        results
          .filter((result) => result.subjectId !== 0 && result.subjectName)
          .map((result) => result.subjectName)
      ),
    ];

    const examCriteriaResponse = await fetch(
      `${apiUrl}/Result/GetExamCriteria_result`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({ studentId }),
      }
    );

    if (!examCriteriaResponse.ok) {
      console.error("Failed to fetch exam criteria");
      return;
    }

    const examCriteriaData = await examCriteriaResponse.json();

    const subCategoryPromises = examCriteriaData.map(async (exam) => {
      const subCategoryResponse = await fetch(
        `${apiUrl}/Result/GetExamSubCategory_Result`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({
            studentId,
            examTypeId: exam.examCategoryId,
          }),
        }
      );

      if (!subCategoryResponse.ok) {
        console.error(
          `Failed to fetch sub-category for exam type ${exam.examType}`
        );
        return [];
      }

      const subCategoryData = await subCategoryResponse.json();
      return {
        examType: exam.examType,
        subCategories: subCategoryData.map((sub) => sub.subExam),
      };
    });

    const examHeadersData = await Promise.all(subCategoryPromises);

    const marksData = {};
    results.forEach((result) => {
      const { subjectName, examType, subExam, obtainedMarks } = result;
      if (result.subjectId !== 0 && subjectName) {
        if (!marksData[subjectName]) marksData[subjectName] = {};
        if (!marksData[subjectName][examType])
          marksData[subjectName][examType] = {};
        marksData[subjectName][examType][subExam] = obtainedMarks;
      }
    });

    const teacherRemarks = results[0]?.teacherRemarks || "N/A";
    const overallGrade = resultsData.grosstotalMarks[0]?.totalGrade || "N/A";
    const totalPercentage =
      resultsData.grosstotalMarks[0]?.overallPercentage || "N/A";

    const updatedStudentProfile = {
      ...studentProfile,
      teacherRemarks,
      grade: overallGrade,
      totalPercentage,
    };

    handlePrint(
      updatedStudentProfile,
      studentAttendance,
      subjectNames,
      examHeadersData,
      marksData,
      results
    );
  } catch (error) {
    console.error("API request error:", error);
  }
};

// Helper function to split text into lines for PDF
const splitTextToLines = (doc, text, maxWidth, maxLines = 3) => {
  const lines = doc.splitTextToSize(text, maxWidth);
  return lines.slice(0, maxLines);
};



  // Generate PDF
 const handlePrint = async (
  student,
  attendance,
  subjectNames,
  examHeadersData,
  marksData,
  results
) => {
  try {
    const doc = new jsPDF();

    // Add border to the page
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const borderMargin = 5;
    doc.setDrawColor(0, 128, 128);
    doc.setLineWidth(0.5);
    doc.rect(borderMargin, borderMargin, pageWidth - 2 * borderMargin, pageHeight - 2 * borderMargin);

    // Logo handling
    let logoDataUrl = null;
    try {
      const response = await fetch(
        `${apiUrl}/FeeReport/GetSchoolName`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({}),
        }
      );

      if (response.ok) {
        const apiData = await response.json();
        const logoImg = apiData?.[0]?.logoImg;
        
        if (logoImg && typeof logoImg === "string" && logoImg.trim() !== "") {
          let cleanedBase64 = logoImg
            .replace(/^data:image\/[a-zA-Z]+;base64,/, "")
            .replace(/\s/g, "")
            .replace(/\n/g, "");
          const paddingLength = (4 - (cleanedBase64.length % 4)) % 4;
          cleanedBase64 += "=".repeat(paddingLength);
          logoDataUrl = `data:image/png;base64,${cleanedBase64}`;

          const apiImg = new Image();
          await new Promise((resolve, reject) => {
            apiImg.onload = () => resolve();
            apiImg.onerror = () => reject(new Error("Failed to load API logo image"));
            apiImg.src = logoDataUrl;
          });

          doc.setFillColor(255, 255, 255);
          doc.rect(15, 20, 25, 25, "F");
          doc.addImage(logoDataUrl, "PNG", 17, 22, 21, 21);
        }
      }
    } catch (error) {
      console.error("Error fetching logo:", error);
    }

    // School header
    const schoolName = sessionStorage.getItem("organizationName") || "JEEVAN ADARSH VIDYALAYA";
    doc.setFont("times", "bold");
    doc.setFontSize(22);
    doc.text(schoolName.replace(/['"]+/g, ""), 105, 25, null, null, "center");

    const address = sessionStorage.getItem("address") || "SCHOOL ID-: 1152593, C-44/151-152, STREET NO.-10, SUDAMAPURI, GAMRI EXT., DELHI-110053";
    const phoneNo = sessionStorage.getItem("phoneNo") || "9968736446";
    
    doc.setFontSize(12);
    doc.setFont("times", "normal");

    let addressFirstLine = address;
    let addressSecondLine = "";
    if (address !== "N/A") {
      const splitIndex = Math.ceil(address.length / 2);
      const splitPoint = address.lastIndexOf(" ", splitIndex) !== -1
        ? address.lastIndexOf(" ", splitIndex)
        : splitIndex;
      addressFirstLine = address.substring(0, splitPoint).trim();
      addressSecondLine = address.substring(splitPoint).trim();
    }
    
    doc.text(addressFirstLine.replace(/['"]+/g, ""), 105, 34, null, null, "center");
    doc.text(addressSecondLine.replace(/['"]+/g, ""), 105, 40, null, null, "center");
    doc.text("Contact No.: " + phoneNo.replace(/['"]+/g, ""), 105, 46, null, null, "center");

    // Separator line
    doc.setDrawColor(0, 128, 128);
    doc.setLineWidth(1.0);
    doc.line(20, 50, 190, 50);

    // Progress Report title
    doc.setFontSize(13);
    doc.setFont("times", "bold");
    doc.text("Progress Report 2024-2025", 105, 56, null, null, "center");

    // Student name
    doc.setFont("times", "bold");
    doc.setFontSize(15);
    doc.text(`${student.studentName || "N/A"}`, 105, 68, null, null, "center");

    // Grey line below student name
    doc.setDrawColor(128, 128, 128);
    doc.setLineWidth(0.3);
    doc.line(70, 72, 140, 72);

    // Student Details
    doc.setFont("times", "normal");
    doc.setFontSize(11);
    const leftMargin = 20;
    const rightMargin = 20;
    const contentWidth = pageWidth - leftMargin - rightMargin;
    const columnWidth = contentWidth / 2;
    const labelWidth = 20;
    const valueWidth = 55;
    const columnGap = 15;
    const valueMargin = 10;

    const leftDetails = [
      ["Father's Name:", student.fathersName || "N/A"],
      ["Mother's Name:", student.mothersName || "N/A"],
      ["Contact No.:", student.fathersMobileNo || "N/A"],
      ["Address:", student.address || "N/A"],
    ];

    const rightDetails = [
      ["Admission No.:", student.admissionNo || "N/A"],
      ["Class & Section:", `${student.className || "N/A"} - ${student.sectionName || "N/A"}`],
      ["Roll No.:", student.rollNo || "N/A"],
      ["Date of Birth:", student.studentDOB || "N/A"],
    ];

    let currentY = 82;
    const lineHeight = 8;

    leftDetails.forEach((detail) => {
      const label = detail[0];
      const value = detail[1];
      if (label === "Address:") {
        const addressLines = doc.splitTextToSize(value, valueWidth);
        doc.setFont("times", "bold");
        doc.text(label, leftMargin, currentY);
        doc.setFont("times", "normal");
        addressLines.forEach((line, i) => {
          if (i < 3) {
            doc.text(line, leftMargin + labelWidth + valueMargin, currentY);
            currentY += lineHeight;
          }
        });
      } else {
        doc.setFont("times", "bold");
        doc.text(label, leftMargin, currentY);
        doc.setFont("times", "normal");
        doc.text(value, leftMargin + labelWidth + valueMargin, currentY);
        currentY += lineHeight;
      }
    });

    currentY = 82;
    rightDetails.forEach((detail) => {
      doc.setFont("times", "bold");
      doc.text(detail[0], leftMargin + columnWidth + columnGap, currentY);
      doc.setFont("times", "normal");
      doc.text(detail[1], leftMargin + columnWidth + columnGap + labelWidth + valueMargin, currentY);
      currentY += lineHeight;
    });

    // Subject-wise marks table
    const subjectHeaders = [
      ["Subject", "Mid Term", "Half Yearly Exam", "Total", "Grade"],
    ];

    // Mock data for subject-wise marks (replace with actual data)
    const subjectData = [
      ["Chemistry", "--", "35", "35", "F"],
      ["Eng", "--", "38", "38", "F"],
      ["Environment", "--", "20", "20", "F"],
      ["Maths", "--", "30", "30", "F"],
    ];

    doc.autoTable({
      head: subjectHeaders,
      body: subjectData,
      startY: 120,
      styles: { fontSize: 9, halign: "center", font: "times" },
      headStyles: {
        fillColor: [0, 128, 128],
        textColor: [255, 255, 255],
        lineWidth: 0.1,
        font: "times",
        fontStyle: "bold",
      },
      theme: "grid",
      margin: { left: 10, right: 10 },
    });

    // Remarks and overall grade
    const remarksY = doc.lastAutoTable.finalY + 10;
    doc.setFont("times", "bold");
    doc.text(`Remarks: ${student.teacherRemarks || "N/A"}`, 20, remarksY);
    doc.text(`Overall Grade: ${student.grade || "N/A"} (${student.totalPercentage || "N/A"}%)`, 150, remarksY, null, null, "right");

    // Attendance table
    const attendanceHeaders = [
      ["Total Days", "Total Present", "Total Absent", "Holiday Count", "Recorded Days", "Attendance Percentage"],
    ];

    // Mock data for attendance (replace with actual data)
    const attendanceData = [
      ["13", "8", "0", "5", "13", "61.54%"],
    ];

    doc.autoTable({
      head: attendanceHeaders,
      body: attendanceData,
      startY: remarksY + 10,
      styles: { fontSize: 9, halign: "center", font: "times" },
      headStyles: {
        fillColor: [0, 128, 128],
        textColor: [255, 255, 255],
        lineWidth: 0.1,
        font: "times",
        fontStyle: "bold",
      },
      theme: "grid",
      margin: { left: 10, right: 10 },
    });

    // Grading scale table
    const gradingHeaders = [
      ["Marks Range in %", "91-100", "81-90", "71-80", "61-70", "51-60", "41-50", "32-40", "32 & Below"],
      ["Grade", "A+", "A", "B+", "B", "C+", "C", "D", "E (Needs Improvement)"],
    ];

    doc.autoTable({
      head: [gradingHeaders[0]],
      body: [gradingHeaders[1]],
      startY: doc.lastAutoTable.finalY + 10,
      styles: { fontSize: 8, halign: "center", font: "times" },
      headStyles: {
        fillColor: [0, 128, 128],
        textColor: [255, 255, 255],
        lineWidth: 0.1,
        font: "times",
        fontStyle: "bold",
      },
      theme: "grid",
      margin: { left: 10, right: 10 },
    });

    // Signatures
    const signatureY = doc.lastAutoTable.finalY + 20;
    doc.setFont("times", "normal");
    doc.text("Signature of Class Teacher", 40, signatureY);
    doc.text("Signature of Principal", 150, signatureY);

    doc.setDrawColor(0, 128, 128);
    doc.setLineWidth(0.3);
    doc.line(30, signatureY + 5, 90, signatureY + 5);
    doc.line(130, signatureY + 5, 190, signatureY + 5);

    // Open PDF
    const pdfUrl = doc.output("bloburl");
    window.open(pdfUrl, "_blank");
  } catch (error) {
    console.error("Error generating PDF:", error);
  }
};



  // useEffect
  useEffect(() => {
    fetchSessions();
    fetchSchoolLogo();
  }, []);

  useEffect(() => {
    if (session && classId && sectionId) {
      fetchResultData();
    }
  }, [session, classId, sectionId]);

  // Render
  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, minHeight: "100vh" }}>
      <LoadingBar progress={loadingBarProgress} color="rgb(75, 108, 183)" height={5} />
      <Card sx={{ maxWidth: "100%", mx: "auto", boxShadow: 3, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <SchoolIcon sx={{ fontSize: 40, color: "rgba(75,108,183,1)", mr: 1 }} />
            <Typography variant="h4" sx={{ fontWeight: "bold", color: "rgba(49, 70, 119, 1)" }}>
              Student Exam Result
            </Typography>
          </Box>
          <Divider sx={{ my: 2 }} />

          <FormControl fullWidth sx={{ mb: 3 }}>
            <Select
              value={session}
              onChange={(e) => setSession(e.target.value)}
              displayEmpty
              sx={{ height: 45 }}
            >
              <MenuItem value="" disabled>
                Select Session
              </MenuItem>
              {sessions.map((item) => (
                <MenuItem key={item.financialYearID} value={item.financialYearID}>
                  {item.finanacialYear}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

        {resultData.length > 0 ? (
  <Box>
    <Typography variant="h6" sx={{ mb: 2, textAlign: "center" }}>
      Result Data
    </Typography>
    <Table sx={{ border: "1px solid #ccc", minWidth: 650 }}>
      <TableHead>
        <TableRow sx={{ background: "linear-gradient(110deg, rgba(75,108,183,1) 11.3%, rgba(24,40,72,1) 99.3%)" }}>
          <TableCell align="center" sx={{ color: "white", fontWeight: 600 }}>Roll No</TableCell>
          <TableCell align="center" sx={{ color: "white", fontWeight: 600 }}>Student Name</TableCell>
          <TableCell align="center" sx={{ color: "white", fontWeight: 600 }}>Total Marks</TableCell>
          <TableCell align="center" sx={{ color: "white", fontWeight: 600 }}>Obtained Marks</TableCell>
          <TableCell align="center" sx={{ color: "white", fontWeight: 600 }}>Grade</TableCell>
          <TableCell align="center" sx={{ color: "white", fontWeight: 600 }}>Action</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {resultData.map((student, index) => (
          <TableRow
            key={index}
            sx={{
              "&:nth-of-type(odd)": { backgroundColor: "#f9f9f9" },
              "&:hover": { backgroundColor: "#f0f0f0" },
            }}
          >
            <TableCell align="center">{student.rollNo}</TableCell>
            <TableCell align="center">{student.studentName}</TableCell>
            <TableCell align="center">{student.totalMaxMarks}</TableCell>
            <TableCell align="center">{student.totalObtainedMarks}</TableCell>
            <TableCell align="center">{student.grade}</TableCell>
           <TableCell align="center">
  <Tooltip title="Print Result">
    <Button
      onClick={() => {
        const student = resultData[0];
        if (student && student.studentId) {
          fetchStudentProfile(student.studentId);  // Changed this line
        } else {
          setSnackbarMessage("Student data not available");
          setOpenSnackbar(true);
        }
      }}
      variant="contained"
      sx={{
        backgroundColor: "rgba(75,108,183,1)",
        "&:hover": { backgroundColor: "rgba(24,40,72,1)" },
      }}
    >
      Print
    </Button>
  </Tooltip>
</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Box>
) : (
  <Typography variant="body1" sx={{ textAlign: "center", mt: 2 }}>
    No result data available.
  </Typography>
)}

        </CardContent>
      </Card>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="warning" sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Result;
